<template>
  <modal
    :name="modalName"
    width="94%"
    height="auto"
    :max-width="560"
    :max-height="760"
    :adaptive="true"
    :scrollable="true"
    :click-to-close="false"
    @opened="focusClose"
    @before-close="beforeClose"
  >
    <section class="communication-action" role="dialog" aria-modal="true" aria-labelledby="communication-action-title">
      <AppModalHeader ref="header" design="new" :title="config.title" eyebrow="Safari communication"
        title-id="communication-action-title" @close="close" />
      <div class="communication-action__body">
        <p class="communication-action__summary">{{ task.client ? task.client.full_name : '—' }} · {{ task.group.name }} · {{ task.title }}</p>
        <p v-if="config.text" class="communication-action__text">{{ config.text }}</p>

        <AppFormField v-if="mode === 'assign'" design="new" label="Employee" label-for="communication-assignee">
          <AppSelect v-model="assignee" design="new" input-id="communication-assignee" aria-label="Employee"
            :options="assignees" :reduce="option => option.id" label="full_name" placeholder="Choose an employee"
            append-to-body :calculate-position="positionDropdown" />
        </AppFormField>

        <template v-if="mode === 'set_response'">
          <fieldset class="communication-action__response-field">
            <legend>Guest response</legend>
            <div class="communication-action__responses">
              <button v-for="option in availableResponses" :key="option.value" type="button"
                class="communication-action__response" :class="[
                  `communication-action__response--${option.tone}`,
                  { 'communication-action__response--selected': response === option.value }
                ]" :aria-pressed="String(response === option.value)" @click="response = option.value">
                <span>{{ option.label }}</span>
                <i v-if="response === option.value" class="pi pi-check" aria-hidden="true" />
              </button>
            </div>
          </fieldset>
          <AppFormField design="new" label="Note" label-for="communication-note">
            <AppTextarea id="communication-note" v-model="note" design="new" maxlength="2000" />
          </AppFormField>
        </template>

        <div v-if="error" class="communication-action__error" role="alert">{{ error }}</div>
      </div>
      <footer class="communication-action__footer">
        <AppButton design="new" theme="dark" :disabled="working" @click.native="close">Cancel</AppButton>
        <AppButton design="new" :theme="config.theme" :disabled="working || !canSubmit" @click.native="submit">
          {{ working ? 'Saving…' : config.submit }}
        </AppButton>
      </footer>
    </section>
  </modal>
</template>

<script>
import { createPopper } from '@popperjs/core'
import { getSafariCommunicationAssignees, postSafariCommunicationAction } from '@/http'
import { RESPONSES, errorMessage } from './model'

const MODES = {
  assign: { title: 'Assign contact', submit: 'Assign', theme: 'success', text: '' },
  complete: { title: 'Confirm delivery', submit: 'Confirm Done', theme: 'success', text: 'Check that the message was delivered before confirming. Done records your name and the time, and cannot be undone from this page.' },
  set_response: { title: 'Guest response', submit: 'Save response', theme: 'success', text: 'This is your own observation, not an automatic WhatsApp or Trengo status. You can update it later.' }
}

export default {
  name: 'CommunicationActionModal',
  props: {
    task: { type: Object, required: true },
    mode: { type: String, required: true }
  },
  data() {
    return {
      modalName: 'ground-ops-communication-action',
      assignees: [],
      assignee: this.task.assignee ? this.task.assignee.id : null,
      response: this.task.response ? this.task.response.result : null,
      note: this.task.response ? this.task.response.note : '',
      working: false,
      error: ''
    }
  },
  computed: {
    config() { return MODES[this.mode] },
    availableResponses() { return this.task.state === 'done' ? RESPONSES.filter(option => option.value !== 'not_delivered') : RESPONSES },
    canSubmit() {
      if (this.mode === 'assign') return Boolean(this.assignee)
      if (this.mode === 'set_response') return Boolean(this.response)
      return true
    }
  },
  mounted() {
    this.$modal.show(this.modalName)
    if (this.mode === 'assign') this.loadAssignees()
  },
  methods: {
    focusClose() { this.$refs.header.focusClose() },
    // The list renders in <body> so the modal's scroll area cannot clip it.
    positionDropdown(dropdownList, component, { width }) {
      dropdownList.classList.add('communication-select-options')
      dropdownList.style.width = width
      dropdownList.style.zIndex = '10020'
      const popper = createPopper(component.$refs.toggle, dropdownList, {
        placement: 'bottom-start',
        modifiers: [
          { name: 'offset', options: { offset: [0, 6] } },
          { name: 'flip', options: { fallbackPlacements: ['top-start'] } },
          { name: 'preventOverflow', options: { padding: 12 } }
        ]
      })
      return () => popper.destroy()
    },
    close() { if (!this.working) this.$modal.hide(this.modalName) },
    beforeClose(event) {
      if (this.working) event.cancel()
      else this.$emit('close')
    },
    async loadAssignees() {
      try {
        this.assignees = (await getSafariCommunicationAssignees()).data
      } catch (error) {
        this.error = 'Could not load employees.'
      }
    },
    payload() {
      if (this.mode === 'assign') return { user_id: this.assignee }
      if (this.mode === 'set_response') return { response: this.response, note: this.note }
      return undefined
    },
    async submit() {
      if (this.working || !this.canSubmit) return
      this.working = true
      this.error = ''
      try {
        const action = this.mode === 'set_response' ? 'response' : this.mode
        const response = await postSafariCommunicationAction(this.task.id, action, this.payload())
        this.$emit('saved', response.data)
        this.working = false
        this.$modal.hide(this.modalName)
      } catch (error) {
        this.error = errorMessage(error, 'Could not save this change. Please try again.')
        this.working = false
      }
    }
  }
}
</script>

<style scoped lang="scss">
.communication-action { color: $ui-text; background: $ui-surface; font-family: $ui-font-body; }
.communication-action__body { display: grid; gap: $ui-space-16; padding: $ui-space-24; }
.communication-action__summary { margin: 0; color: $ui-ink; font-weight: 600; }
.communication-action__text { margin: 0; color: $ui-muted; line-height: 1.5; }
.communication-action__response-field { min-width: 0; margin: 0; padding: 0; border: 0; }
.communication-action__response-field legend { margin-bottom: $ui-space-8; padding: 0; color: $ui-text; font-weight: 600; }
.communication-action__responses { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: $ui-space-8; }
.communication-action__response {
  display: flex; min-width: 0; min-height: 44px; align-items: center; justify-content: space-between; gap: $ui-space-8;
  padding: $ui-space-8 $ui-space-12; border: 1px solid $ui-line-strong; border-radius: $ui-control-radius;
  background: $ui-surface; color: $ui-text; font: inherit; text-align: left; cursor: pointer;
}
.communication-action__response:hover { border-color: currentColor; }
.communication-action__response:focus-visible { outline: 2px solid $ui-blue; outline-offset: 2px; }
.communication-action__response--red { color: $ui-red; }
.communication-action__response--amber { color: $ui-amber; }
.communication-action__response--green { color: $ui-green; }
.communication-action__response--selected { border-color: currentColor; font-weight: 600; }
.communication-action__response--selected.communication-action__response--red { background: $ui-red-soft; }
.communication-action__response--selected.communication-action__response--neutral { background: $ui-surface-soft; }
.communication-action__response--selected.communication-action__response--amber { background: $ui-amber-soft; }
.communication-action__response--selected.communication-action__response--green { background: $ui-green-soft; }
.communication-action__error { padding: $ui-space-16; border-radius: $ui-control-radius; color: $ui-red; background: $ui-red-soft; }
.communication-action__footer { display: flex; justify-content: flex-end; gap: $ui-space-12; padding: $ui-space-16 $ui-space-24; border-top: 1px solid $ui-line; }
</style>

<style lang="scss">
.vs__dropdown-menu.communication-select-options[data-popper-placement] {
  max-height: 260px;
  padding: $ui-space-4;
  border: 1px solid $ui-line-strong;
  border-radius: 10px;
  background: $ui-surface;
  box-shadow: 0 12px 30px rgba(23, 36, 58, .16);
  color: $ui-text;
  font-family: $ui-font-body;
}

.vs__dropdown-menu.communication-select-options[data-popper-placement] .vs__dropdown-option {
  display: flex;
  min-height: 38px;
  align-items: center;
  margin: 1px 0;
  padding: $ui-space-8 $ui-space-12;
  border-radius: 8px;
  color: $ui-text;
  font-size: $ui-font-size-body;
}

.vs__dropdown-menu.communication-select-options[data-popper-placement] .vs__dropdown-option--highlight,
.vs__dropdown-menu.communication-select-options[data-popper-placement] .vs__dropdown-option--highlight .select__item {
  background: $ui-surface-soft;
  color: $ui-blue;
}

.vs__dropdown-menu.communication-select-options[data-popper-placement] .vs__dropdown-option--selected {
  font-weight: 600;
}
</style>
